import React, { ChangeEvent, Reducer, useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import { Button, Grid, InputAdornment, MenuItem, TextField } from '@mui/material';
import Alert from '@mui/material/Alert';
import classNames from 'classnames';
import stylesDropzone from 'components/applications/ApplicationQuestionsFormSection/FileQuestionFormItem/FileQuestionFormItem.module.scss';
import reducer, {
  ExpenseReportAction,
  ExpenseReportActionType,
  ExpenseReportState,
  initialState,
} from 'components/expense/ExpenseReportPage/ExpenseReportReducer';
import FileListDelete from 'components/expense/ExpenseReportPage/FileListDelete';
import FileListDownload from 'components/expense/ExpenseReportPage/FileListDownload';
import DateFormItem from 'components/shared/DateFormItem';
import Modal from 'components/shared/Modal';
import useRequest from 'hooks/useRequest';
import { DropzoneArea } from 'mui-file-dropzone';
import { useSnackbar } from 'notistack';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useHistory } from 'react-router';
import ExpenseService from 'services/api/ExpenseService';
import routes from 'store/configs/Routes';
import { ModalProps } from 'store/types/ComponentProps';
import {
  ExpenseCategory,
  ExpenseFile,
  ExpenseForm,
  ExpenseItemSaveRequest,
  ExpenseReportCreateRequest,
  ExpenseReportItemCreateRequest,
  ExpenseReportItemUpdateRequest,
} from 'store/types/Expense';
import {
  defaultFormProps,
  FREEFORM_TEXT_MAX_LENGTH,
  getMaxLengthValidationRule,
  getRequiredValidationRule,
  getValidationProps,
  NUMBER_WHOLE_PLUS_DECIMAL_REGEXP,
} from 'util/Form';
import { defaultGridContainerProps, defaultGridItemProps, defaultSnackbarErrorProps } from 'util/Layout';

import commonStyles from 'styles/common.module.scss';
import styles from 'components/expense/Expense.module.scss';

interface ExpenseFormModalProps extends ModalProps {
  expenseForm?: ExpenseForm | null;
  isReadOnly: boolean;
  defaultValues: ExpenseForm;
  headerBlock?: string;
  onSubmit: () => void;
}

const getDefaultValues = (
  expenseFormModal: ExpenseForm,
  pageLoad: boolean,
  selectedCategory?: ExpenseCategory,
  amount = ''
): ExpenseForm => {
  if (selectedCategory) {
    expenseFormModal.expense.category = selectedCategory;
  }

  if (amount && expenseFormModal?.expense) {
    expenseFormModal.expense.amount = amount;
  }

  const expense = {
    ...expenseFormModal.expense,
    quantity: expenseFormModal?.expense?.quantity || (pageLoad ? '' : null),
    rate: expenseFormModal?.expense?.rate || (pageLoad ? '' : null),
    amount: expenseFormModal?.expense?.amount || (pageLoad ? '' : '0'),
    files: expenseFormModal?.expense?.receiptFileUploads ?? [],
  };

  return {
    ...expenseFormModal,
    expense: expense,
  } as ExpenseForm;
};

const ExpenseFormModal: React.FunctionComponent<ExpenseFormModalProps> = ({
  expenseForm,
  isReadOnly,
  defaultValues,
  headerBlock,
  onClose,
  onSubmit,
  open,
}) => {
  const MAX_FILE_LIMIT = 1;
  const MIN_FILE_SIZE = 0;
  const MAX_FILE_SIZE = 2000000;

  const form = useForm<ExpenseForm>({ ...defaultFormProps, defaultValues, shouldUnregister: false });
  const {
    control,
    handleSubmit,
    reset,
    resetField,
    formState: { errors, isDirty, isValid },
  } = form;

  const [{ amount, selectedCategory, loading: pageLoading }, dispatch] = useReducer<
    Reducer<ExpenseReportState, ExpenseReportAction>
  >(reducer, initialState);

  const {
    data: categoryOptions,
    error: categoryError,
    loading: categoryLoading,
  } = useRequest<ExpenseCategory[]>(ExpenseService.getExpenseCategories);

  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const [fileDeleted, setFileDeleted] = useState(false);
  const [key, setKey] = useState('');
  const [showMinFileSizeError, setShowMinFileSizeError] = useState(false);

  const submitButtonDisabled: boolean = useMemo(
    () => !fileDeleted && (!isDirty || !isValid || categoryLoading || pageLoading),
    [fileDeleted, isDirty, isValid, categoryLoading, pageLoading]
  );

  useEffect(() => {
    if (categoryOptions && expenseForm?.expense?.category) {
      dispatch({
        type: ExpenseReportActionType.SetCategory,
        payload: { selectedCategory: expenseForm.expense.category },
      });
    }
  }, [categoryOptions, expenseForm?.expense?.category]);

  useEffect(() => {
    if (expenseForm) {
      if (expenseForm?.expense) {
        if (expenseForm.expense?.amount !== '') {
          expenseForm.expense.amount = Number(expenseForm.expense.amount).toFixed(2);
        }
      } else {
        expenseForm.expense = defaultValues.expense;
      }

      reset(getDefaultValues(expenseForm, true));

      dispatch({
        type: ExpenseReportActionType.SetAmount,
        payload: { amount: expenseForm.expense.amount },
      });
    }
  }, [defaultValues.expense, expenseForm, reset]);

  const handleModalClose = useCallback(() => {
    onClose();

    if (expenseForm) {
      reset(getDefaultValues(expenseForm, true));
    } else {
      reset(defaultValues);
    }
    setFileDeleted(false);
    setKey(Date.now().toString());
  }, [defaultValues, expenseForm, onClose, reset]);

  const handleFileChange = useCallback(
    (onChange) => (loadedFiles: File[]) => {
      if (loadedFiles.length > 0) {
        setShowMinFileSizeError(loadedFiles[0].size === MIN_FILE_SIZE);
      } else {
        setShowMinFileSizeError(false);
      }
      onChange(loadedFiles);
    },
    []
  );

  const handleFileDelete = useCallback(() => {
    if (expenseForm?.expense?.receiptFile) {
      setFileDeleted(true);
    }
  }, [expenseForm?.expense?.receiptFile]);

  const uploadFile = useCallback((data: ExpenseForm) => {
    if (data?.expense?.receiptFileUploads?.length === 1) {
      return ExpenseService.uploadReceiptFile(data.expense.receiptFileUploads[0], data.id);
    } else {
      return Promise.resolve('');
    }
  }, []);

  const populateExpenseItem = (formData: ExpenseForm) => {
    return {
      date: formData.expense.date,
      categoryId: formData.expense?.category?.id ?? '',
      isQtyRateCategory: formData.expense?.category?.isQtyRateCategory,
      quantity: formData.expense.quantity,
      rate: formData.expense.rate,
      amount: formData.expense.amount,
      memo: formData.expense.memo,
      receiptFile: formData.expense.receiptFile,
      fileId: formData.expense?.receiptFile?.id ?? '',
    } as ExpenseItemSaveRequest;
  };

  const createExpenseReport = useCallback(
    (formData: ExpenseForm, closeModal: boolean) => {
      const expenseReportCreateRequest = {
        delegatorProfileId: formData.delegatorProfileId,
        memo: formData.memo,
        expense: populateExpenseItem(formData),
      } as ExpenseReportCreateRequest;

      ExpenseService.createExpenseReport(expenseReportCreateRequest)
        .then((newReportId) => {
          onSubmit();
          dispatch({
            type: ExpenseReportActionType.SetLoading,
            payload: { loading: false },
          });

          if (closeModal) {
            handleModalClose();
          } else {
            formData.id = newReportId;
            formData.expense = defaultValues.expense;
            reset(formData);
          }
          enqueueSnackbar('Expense report successfully created', { variant: 'success' });

          history.push(`${routes.expenseReport}/${newReportId}`);
        })
        .catch((errorMessage: string) => {
          dispatch({
            type: ExpenseReportActionType.SetLoading,
            payload: { loading: false },
          });
          enqueueSnackbar(errorMessage, defaultSnackbarErrorProps);
        });
    },
    [defaultValues.expense, enqueueSnackbar, handleModalClose, history, onSubmit, reset]
  );

  const createExpenseItem = useCallback(
    (formData: ExpenseForm, closeModal: boolean) => {
      const expenseReportItemCreateRequest = {
        id: formData.id,
        delegatorProfileId: formData.delegatorProfileId,
        expense: populateExpenseItem(formData),
      } as ExpenseReportItemCreateRequest;

      ExpenseService.createExpenseItem(expenseReportItemCreateRequest)
        .then(() => {
          onSubmit();
          dispatch({
            type: ExpenseReportActionType.SetLoading,
            payload: { loading: false },
          });
          if (closeModal) {
            handleModalClose();
          } else {
            dispatch({
              type: ExpenseReportActionType.SetAmount,
              payload: { amount: '' },
            });

            formData.expense = defaultValues.expense;
            reset(formData);
          }
          enqueueSnackbar('Expense successfully created', { variant: 'success' });
        })
        .catch((errorMessage: string) => {
          dispatch({
            type: ExpenseReportActionType.SetLoading,
            payload: { loading: false },
          });
          enqueueSnackbar(errorMessage, defaultSnackbarErrorProps);
        });
    },
    [defaultValues.expense, enqueueSnackbar, handleModalClose, onSubmit, reset]
  );

  const updateExpenseItem = useCallback(
    (formData: ExpenseForm) => {
      const expenseReportUpdateRequest = {
        id: formData.id,
        delegatorProfileId: formData.delegatorProfileId,
        expense: populateExpenseItem(formData),
      } as ExpenseReportItemUpdateRequest;

      ExpenseService.updateExpenseItem(formData.expense.id, expenseReportUpdateRequest)
        .then(() => {
          onSubmit();
          dispatch({
            type: ExpenseReportActionType.SetLoading,
            payload: { loading: false },
          });
          handleModalClose();
          enqueueSnackbar('Expense successfully updated', { variant: 'success' });
        })
        .catch((errorMessage: string) => {
          dispatch({
            type: ExpenseReportActionType.SetLoading,
            payload: { loading: false },
          });
          enqueueSnackbar(errorMessage, defaultSnackbarErrorProps);
        });
    },
    [enqueueSnackbar, handleModalClose, onSubmit]
  );

  const handleFormSubmit = useCallback(
    (data: ExpenseForm, closeModal: boolean) => {
      const formData = getDefaultValues(data, false, selectedCategory, amount);

      dispatch({
        type: ExpenseReportActionType.SetLoading,
        payload: { loading: true },
      });

      if (fileDeleted && formData.expense?.receiptFile) {
        formData.expense.receiptFile = null;
      }

      uploadFile(formData)
        .then((newFileId) => {
          if (newFileId) {
            formData.expense.receiptFile = { id: newFileId } as ExpenseFile;
          }

          const isNewExpenseReport = !formData?.id;
          const isExistingExpenseItem = formData.expense.id;

          if (isNewExpenseReport) {
            createExpenseReport(formData, closeModal);
          } else if (isExistingExpenseItem) {
            updateExpenseItem(formData);
          } else {
            createExpenseItem(formData, closeModal);
          }
        })
        .catch((errorMessage: string) => {
          dispatch({
            type: ExpenseReportActionType.SetLoading,
            payload: { loading: false },
          });
          enqueueSnackbar(errorMessage, defaultSnackbarErrorProps);
        });
    },
    [
      amount,
      createExpenseItem,
      createExpenseReport,
      enqueueSnackbar,
      fileDeleted,
      selectedCategory,
      updateExpenseItem,
      uploadFile,
    ]
  );

  const handleFormSubmitModalOpen = useCallback(
    (data: ExpenseForm) => {
      handleFormSubmit(data, false);
    },
    [handleFormSubmit]
  );

  const handleFormSubmitModalClosed = useCallback(
    (data: ExpenseForm) => {
      handleFormSubmit(data, true);
    },
    [handleFormSubmit]
  );

  const handleCategoryChange = useCallback(
    (onChange) => (e: ChangeEvent<HTMLInputElement>) => {
      onChange(e.target.value);

      if (categoryOptions) {
        const category = categoryOptions.find((item) => item.id === e.target.value);

        dispatch({
          type: ExpenseReportActionType.SetCategory,
          payload: { selectedCategory: category },
        });

        if (category?.isQtyRateCategory) {
          dispatch({
            type: ExpenseReportActionType.SetAmount,
            payload: {},
          });
          resetField('expense.amount');
        }
      }
    },
    [categoryOptions, resetField]
  );

  const handleAmountChange = useCallback(
    (onChange) => (e: ChangeEvent<HTMLInputElement>) => {
      dispatch({
        type: ExpenseReportActionType.SetAmount,
        payload: { amount: e.target.value },
      });

      onChange(e.target.value);
    },
    []
  );

  return categoryError ? (
    <Alert severity={'error'} className={commonStyles.alert}>
      {categoryError}
    </Alert>
  ) : (
    <Modal
      title={`${expenseForm?.expense?.id === '' ? 'New ' : 'Edit '}Expense Item`}
      keepMounted={true}
      open={open}
      loading={categoryLoading || pageLoading}
      onClose={handleModalClose}
      maxWidth={'md'}
      actions={
        <>
          {!isReadOnly && (
            <Button
              color={'secondary'}
              variant={'outlined'}
              disabled={categoryLoading || pageLoading}
              onClick={onClose}
            >
              {'Cancel'}
            </Button>
          )}

          {expenseForm?.expense?.id === '' && !isReadOnly && (
            <Button
              color={'secondary'}
              variant={'contained'}
              disabled={submitButtonDisabled}
              className={styles.createNewExpenseButton}
              onClick={handleSubmit(handleFormSubmitModalOpen)}
            >
              {'Add & Create New'}
            </Button>
          )}

          {!isReadOnly && (
            <Button
              color={'secondary'}
              variant={'contained'}
              disabled={submitButtonDisabled}
              onClick={handleSubmit(handleFormSubmitModalClosed)}
            >
              {`${expenseForm?.expense?.id === '' ? 'Add' : 'Save'}`}
            </Button>
          )}
        </>
      }
    >
      <FormProvider {...form}>
        <Grid {...defaultGridContainerProps}>
          {headerBlock && (
            <Grid {...defaultGridItemProps}>
              <div dangerouslySetInnerHTML={{ __html: headerBlock }} />
            </Grid>
          )}
          <Grid {...defaultGridItemProps}>
            <Grid {...defaultGridItemProps} sm={12} md={4}>
              <DateFormItem fieldName={'expense.date'} label={'Date'} disabled={isReadOnly} />
            </Grid>
          </Grid>
          {!!categoryOptions?.length && (
            <Grid {...defaultGridItemProps} sm={12} md={6}>
              <Controller
                name={'expense.category.id'}
                control={control}
                rules={{
                  required: getRequiredValidationRule('Category'),
                }}
                render={({ field: { onChange, value, onBlur } }) => (
                  <TextField
                    {...getValidationProps('expense.category.id', errors)}
                    onBlur={onBlur}
                    select={true}
                    label={'Category'}
                    value={value}
                    onChange={handleCategoryChange(onChange)}
                    required={true}
                    disabled={isReadOnly}
                    SelectProps={{
                      displayEmpty: true,
                    }}
                  >
                    {categoryOptions.map(({ id, name }) => (
                      <MenuItem key={`expense-category-${id}`} value={id}>
                        {name}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>
          )}
          {selectedCategory?.isQtyRateCategory && (
            <>
              <Grid {...defaultGridItemProps} sm={12} md={3}>
                <Controller
                  name={'expense.quantity'}
                  control={control}
                  rules={{
                    required: getRequiredValidationRule('Quantity', false, selectedCategory?.isQtyRateCategory),
                    min: {
                      value: 0.01,
                      message: 'Please input valid quantity',
                    },
                    pattern: {
                      value: NUMBER_WHOLE_PLUS_DECIMAL_REGEXP,
                      message: 'Please input valid quantity',
                    },
                  }}
                  render={({ field }) => {
                    return (
                      <TextField
                        label={'Quantity'}
                        placeholder={'0'}
                        required={selectedCategory?.isQtyRateCategory}
                        disabled={isReadOnly}
                        {...field}
                        {...getValidationProps('expense.quantity', errors)}
                        InputProps={{
                          inputProps: { min: 0 },
                        }}
                        InputLabelProps={{ shrink: true }}
                      />
                    );
                  }}
                />
              </Grid>
              <Grid {...defaultGridItemProps} sm={12} md={3}>
                <Controller
                  name={'expense.rate'}
                  control={control}
                  rules={{
                    required: getRequiredValidationRule('Rate', false, selectedCategory?.isQtyRateCategory),
                    min: {
                      value: 0.01,
                      message: 'Please input valid rate',
                    },
                    pattern: {
                      value: NUMBER_WHOLE_PLUS_DECIMAL_REGEXP,
                      message: 'Please input valid rate',
                    },
                  }}
                  render={({ field }) => {
                    return (
                      <TextField
                        label={'Rate'}
                        placeholder={'0'}
                        required={selectedCategory?.isQtyRateCategory}
                        disabled={isReadOnly}
                        {...field}
                        {...getValidationProps('expense.rate', errors)}
                        InputProps={{
                          inputProps: { min: 0 },
                          startAdornment: <InputAdornment position={'start'}>$</InputAdornment>,
                        }}
                      />
                    );
                  }}
                />
              </Grid>
            </>
          )}
          <Grid {...defaultGridItemProps} sm={12} md={4}>
            <Controller
              name={'expense.amount'}
              control={control}
              rules={{
                required: getRequiredValidationRule('Amount', false, !selectedCategory?.isQtyRateCategory),
                min: {
                  value: 0.01,
                  message: 'Please input valid amount',
                },
                pattern: {
                  value: NUMBER_WHOLE_PLUS_DECIMAL_REGEXP,
                  message: 'Please input valid amount',
                },
              }}
              render={({ field: { onChange, onBlur } }) => (
                <TextField
                  onBlur={onBlur}
                  label={'Amount'}
                  placeholder={'0'}
                  value={amount}
                  onChange={handleAmountChange(onChange)}
                  required={!selectedCategory?.isQtyRateCategory}
                  {...getValidationProps('expense.amount', errors)}
                  disabled={isReadOnly || selectedCategory?.isQtyRateCategory}
                  InputProps={{
                    inputProps: { min: 0 },
                    startAdornment: <InputAdornment position={'start'}>$</InputAdornment>,
                  }}
                />
              )}
            />
          </Grid>
          <Grid {...defaultGridItemProps}>
            <Controller
              name={'expense.memo'}
              control={control}
              rules={{
                required: getRequiredValidationRule('Memo'),
                maxLength: getMaxLengthValidationRule(FREEFORM_TEXT_MAX_LENGTH),
              }}
              render={({ field }) => (
                <TextField
                  label={'Memo'}
                  {...field}
                  {...getValidationProps('expense.memo', errors)}
                  required={true}
                  disabled={isReadOnly}
                  multiline={true}
                  minRows={3}
                />
              )}
            />
          </Grid>
          {!isReadOnly && (
            <Grid {...defaultGridItemProps} sm={'auto'} className={styles.fileUploadSection}>
              <h2>{'Attach Receipt'}</h2>

              <Controller
                name={'expense.receiptFileUploads'}
                control={control}
                render={({ field: { onChange, value } }) => (
                  <DropzoneArea
                    key={`upload-${key}`}
                    onChange={handleFileChange(onChange)}
                    onDelete={onChange}
                    dropzoneText={'Upload Files'}
                    maxFileSize={MAX_FILE_SIZE}
                    filesLimit={MAX_FILE_LIMIT}
                    fileObjects={value}
                    showAlerts={['error']}
                    clearOnUnmount={true}
                    showPreviews={true}
                    showPreviewsInDropzone={false}
                    useChipsForPreview={true}
                    previewText={''}
                    classes={{
                      root: classNames(stylesDropzone.dropzone),
                      textContainer: stylesDropzone.dropzoneTextContainer,
                      text: stylesDropzone.dropzoneText,
                      icon: stylesDropzone.dropzoneIcon,
                    }}
                    previewGridClasses={{
                      container: stylesDropzone.previewContainer,
                      item: stylesDropzone.previewImageContainer,
                    }}
                    dropzoneProps={{ noDrag: true }}
                    previewChipProps={{ className: stylesDropzone.chip }}
                    alertSnackbarProps={{
                      anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                    }}
                  />
                )}
              />

              {showMinFileSizeError && (
                <Alert severity={'error'} className={commonStyles.alert}>
                  File size must be greater than zero bytes.
                </Alert>
              )}
            </Grid>
          )}

          {expenseForm?.expense?.receiptFile && !fileDeleted && (
            <>
              <Grid {...defaultGridItemProps} xs={10} className={styles.value}>
                <FileListDownload
                  expenseReportId={expenseForm.id}
                  expenseId={expenseForm.expense.id}
                  filename={expenseForm.expense.receiptFile.name}
                />
              </Grid>
              <Grid {...defaultGridItemProps}>
                <FileListDelete
                  file={
                    {
                      id: expenseForm.expense.receiptFile.id,
                      name: expenseForm.expense.receiptFile.name,
                    } as ExpenseFile
                  }
                  isReadOnly={isReadOnly}
                  onDeleteFile={handleFileDelete}
                />
              </Grid>
            </>
          )}
        </Grid>
      </FormProvider>
    </Modal>
  );
};

export default ExpenseFormModal;
